import { AddFilter } from "@assets/icons";
import Grid from "@mui/material/Grid2";
import Button from "@common/components/Button";
import CSVMenu, { CSVMenuProps } from "@common/components/CSV/CSVMenu";
import { FiltersTypeImpl } from "@common/utils/filters";
import { isAccountReadonly } from "@common/utils/user";
import { Add, CloudUploadOutlined, Replay } from "@mui/icons-material";
import { IconButton, Tooltip, styled } from "@mui/material";
import FilterColumn from "@pages/Assets/common/FilterColumn";
import { useAssetNavigationParams } from "@pages/Assets/common/const";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";
import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { FieldValueProvider } from "../Header/SearchBar/context/fieldValue";
import ToolButtonsContainer from "../ToolButtonsContainer";
import AddFiltersDrawer from "./AddFiltersDrawer";
import { countOtherFilters as countOtherFiltersFunc } from "./helpers";
import { EntityTypeList } from "@pages/BI/Audit/const";
import {
  useSearchFilterMode,
  SearchFilterMode,
} from "@common/hooks/useSearchFilterMode";
import AdvancedSearch from "../Header/SearchBar/Search/AdvancedSearch";
import { SearchProvider } from "../Header/SearchBar/context/search";
import {
  getSearchTypeFromLocation,
  useInitializeSearchType,
} from "../Header/SearchBar/Search/hook/useInitializeSearchType";
import { AuthenticatedUser } from "@services/user/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { useCustomSearchParams } from "@services/common/hooks/useSearchParams";
import { useLocation } from "react-router-dom";

export type FilteringBarProps<
  T extends UserWidgetType | UserOtherType = UserWidgetType | UserOtherType
> = {
  filters: FilterDefinition[];
  setFilters: (filters: FilterDefinition[]) => void;
  setLocalStorageFilter?: (value: FilterDefinition[] | undefined) => void;
  type: T;
  entity?: T extends "AuditTrails"
    ? { entityType?: EntityTypeList[] | EntityTypeList }
    : {};
  refetch: () => void;
  csvExportProps?: CSVMenuProps;
  deleteButton?: React.JSX.Element;
  editButton?: React.JSX.Element;
  customButton?: React.JSX.Element;
  onAdd?: () => void;
  onImport?: () => void;
  disableAddButton?: boolean;
  hideFilters?: boolean;
  hideOtherFilters?: boolean;
  hideColumnButton?: boolean;
  loading?: boolean;
  closeTicketButton?: React.JSX.Element;
  linkComponent?: React.JSX.Element;
  addButtonTooltip?: string;
};

const ClearButton = styled(Button)(({ theme }) => ({
  color: theme.palette.common.grey.g828282,
}));

const FilteringBar = (props: FilteringBarProps) => {
  const {
    filters,
    setFilters,
    setLocalStorageFilter,
    type,
    refetch,
    onAdd,
    onImport,
    disableAddButton = true,
    addButtonTooltip = "Add",
    deleteButton,
    editButton,
    customButton,
    closeTicketButton,
    hideFilters = false,
    hideOtherFilters = false,
    hideColumnButton = false,
    loading = false,
    linkComponent,
  } = props;
  const [open, setOpen] = useState<boolean>(false);
  const clearFiltersRef = useRef<HTMLDivElement>(null);
  const [showRTStatusFilter, setShowRTStatusFilter] = useState<boolean>(true);
  const rtstatusRef = useRef<HTMLDivElement>(null);
  const [showTicketsFilter, setShowTicketsFilter] = useState<boolean>(true);
  const ticketsRef = useRef<HTMLDivElement>(null);
  const [showResetFilter, setShowResetFilter] = useState<boolean>(true);
  const mode = useSearchFilterMode();
  const [_, setSearchParams] = useCustomSearchParams();
  const { state } = useLocation();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const [, , allAccounts] = useUserSelectedAccounts();

  const { fromAccount: accountId } =
    useAssetNavigationParams().navigationParams;
  const searchTypeFromLocation = getSearchTypeFromLocation();

  const listener = () => {
    setShowRTStatusFilter(!isColliding(clearFiltersRef, rtstatusRef));
    setShowTicketsFilter(!isColliding(clearFiltersRef, ticketsRef));
  };

  useEffect(() => {
    setShowRTStatusFilter(!isColliding(clearFiltersRef, rtstatusRef));
    setShowTicketsFilter(!isColliding(clearFiltersRef, ticketsRef));
    addEventListener("resize", listener);
    addEventListener("load", listener);
    return () => {
      removeEventListener("resize", listener);
      removeEventListener("load", listener);
    };
  }, []);

  useEffect(() => {
    displayResetFilter();
  }, [filters]);

  const displayResetFilter = () => {
    if (filters.length > 0) setShowResetFilter(true);
    else setShowResetFilter(false);
  };

  useEffect(() => {
    if (hideFilters === true) {
      setFilters([]);
    }
  }, [hideFilters]);

  const countOtherFilters = countOtherFiltersFunc(filters);

  const isColliding = (
    elem1: RefObject<HTMLDivElement | null>,
    elem2: RefObject<HTMLDivElement | null>
  ) => {
    if (!elem1.current || !elem2.current) {
      return false;
    }

    const elem1Left = elem1.current?.getBoundingClientRect().left as number;
    const elem2Right = elem2.current?.getBoundingClientRect().right as number;
    return elem1Left < elem2Right;
  };

  const handleClickFilter = (
    filters: FilterDefinition[],
    filterType: FilterTypes,
    filterValue: string,
    payload?: FilterDefinition["payload"]
  ) => {
    if (!filters.some((f) => f.filterType === filterType)) {
      return [...filters, { filterType, filterValue, payload }];
    } else {
      if (
        filters.some(
          (f) => f.filterType === filterType && f.filterValue === filterValue
        ) &&
        payload === undefined
      )
        return [...filters.filter((f) => f.filterType !== filterType)];
      else
        return [
          ...filters.filter((f) => f.filterType !== filterType),
          { filterType, filterValue, payload },
        ];
    }
  };

  const handleClickClear = () => {
    if (mode === SearchFilterMode.FILTER) {
      const clearedFilters = filters
        .map((filter) => {
          if (type === FiltersTypeImpl.Meetings) {
            switch (filter.filterType) {
              case "time":
                return {
                  filterType: "time",
                  filterValue: "For the next 12 hours",
                  payload: {},
                };
              default:
                return { ...filter, filterValue: "", payload: {} };
            }
          }
          return {
            filterType: filter.filterType,
            filterValue: "",
            payload: {},
          };
        })
        .filter(
          (x) => x.filterValue || Object.keys(x.payload ?? {}).length > 0
        );
      if (allAccounts.length === 1) {
        setFilters([
          ...clearedFilters,
          { filterType: "accountId", filterValue: allAccounts[0].id },
        ]);
      } else {
        setFilters(clearedFilters);
      }
      setLocalStorageFilter && setLocalStorageFilter(clearedFilters);
    } else {
      setSearchParams({ mode: "search" }, { replace: true, state: state });
    }
  };

  const handleClose = (filters: FilterDefinition[] | undefined) => {
    setOpen(false);
    if (filters) {
      setFilters(filters);
      setLocalStorageFilter && setLocalStorageFilter(filters);
    }
  };

  const handleClickRefresh = () => {
    refetch();
  };

  const isReadOnly = useCallback(() => {
    if (accountId) {
      return isAccountReadonly(allAccounts ?? [], accountId);
    }
    return false;
  }, [allAccounts, accountId]);

  const isDrawerFilterContext = (
    type: UserWidgetType | UserOtherType,
    pathname: string
  ) => {
    const drawerFilterTypes: Array<UserWidgetType | UserOtherType> = [
      "AuditTrails",
    ];
    const drawerFilterPath: string[] = ["/configuration/users"];

    return (
      drawerFilterTypes.includes(type) && drawerFilterPath.includes(pathname)
    );
  };

  const isFilterMode =
    mode === SearchFilterMode.FILTER ||
    searchTypeFromLocation === undefined ||
    isDrawerFilterContext(type, pathname);

  return (
    <>
      <Grid
        container
        direction="row"
        wrap="nowrap"
        ref={clearFiltersRef}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          width: "auto",
          flexGrow: 1,
          position: "sticky",
          right: 0,
          px: 2,
        }}
      >
        <Grid
          sx={{
            minWidth: "max-content",
            gap: 1,
            display: "flex",
          }}
        >
          {!hideFilters && (
            <Button
              onClick={() => setOpen(true)}
              startIcon={<AddFilter />}
              variant="outlined"
              ignoreLocalization
            >
              {isFilterMode
                ? t("Filters ({{COUNTER}})", { COUNTER: countOtherFilters })
                : t("Search Criteria")}
            </Button>
          )}
          {showResetFilter && !hideFilters && (
            <ClearButton onClick={handleClickClear} variant="text">
              {t("Clear {{MODE}}", {
                MODE: isFilterMode ? "filters" : "search",
              })}
            </ClearButton>
          )}
        </Grid>
        <Grid
          container
          wrap="nowrap"
          spacing={1.25}
          sx={{
            width: "100%",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {linkComponent && <Grid>{linkComponent}</Grid>}
          {deleteButton && <Grid>{deleteButton}</Grid>}
          {editButton && <Grid>{editButton}</Grid>}
          {closeTicketButton && <Grid>{closeTicketButton}</Grid>}
          {customButton && <Grid>{customButton}</Grid>}
          {onAdd !== undefined && (
            <ToolButtonsContainer>
              {type === FiltersTypeImpl.Workflows ? (
                <Button
                  disabled={disableAddButton}
                  onClick={onAdd}
                  startIcon={<Add />}
                  variant="outlined"
                  ignoreLocalization
                >
                  {t("Add new workflow")}
                </Button>
              ) : (
                <Tooltip title={t(addButtonTooltip) as string} arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={onAdd}
                      disabled={disableAddButton || isReadOnly()}
                    >
                      <Add />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </ToolButtonsContainer>
          )}

          {onImport !== undefined && (
            <ToolButtonsContainer>
              <Tooltip title={t("Import") as string} arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={onImport}
                    disabled={disableAddButton || isReadOnly()}
                  >
                    <CloudUploadOutlined />
                  </IconButton>
                </span>
              </Tooltip>
            </ToolButtonsContainer>
          )}
          {props.csvExportProps && (
            <ToolButtonsContainer>
              <CSVMenu {...props.csvExportProps} />
            </ToolButtonsContainer>
          )}
          {!hideColumnButton && (
            <ToolButtonsContainer>
              <FilterColumn />
            </ToolButtonsContainer>
          )}

          <ToolButtonsContainer>
            <Tooltip title={t("Refresh") as string} arrow>
              <span>
                <IconButton size="small" onClick={handleClickRefresh}>
                  <Replay />
                </IconButton>
              </span>
            </Tooltip>
          </ToolButtonsContainer>
        </Grid>

        <FieldValueProvider>
          <SearchProvider closeDrawer={() => setOpen(false)}>
            {isFilterMode ? (
              <AddFiltersDrawer
                open={open}
                onClose={handleClose}
                filters={filters}
                handleClickFilter={handleClickFilter}
                type={type}
                entity={props.entity}
                handleClickClear={handleClickClear}
              />
            ) : (
              <SearchDrawer open={open} />
            )}
          </SearchProvider>
        </FieldValueProvider>
      </Grid>
    </>
  );
};

const SearchDrawer = ({ open }: { open: boolean }) => {
  const [user] = useAuthenticatedUser();
  useInitializeSearchType(user);
  return (
    <AdvancedSearch
      open={open}
      filterState={{
        search: "",
        currentField: "",
      }}
    />
  );
};

export default FilteringBar;
